/**
 * Internal Service Service
 *
 * Manages internal services for typing centers via the backend API.
 */

import ApiService from './api.service';
import { isThaklisServicesEnabled } from '../hooks/useThaklisServicesOff';

// Labels for the old fixed category codes. Category pickers now use the TC's own categories
// (tc-service-category.service); this list only labels Thaklis services and not-yet-backfilled rows.
export const SERVICE_CATEGORIES = [
  { value: 'ICP', label: 'ICP - Immigration' },
  { value: 'ICA', label: 'ICA - Emirates ID' },
  { value: 'MOHRE', label: 'MOHRE - Labour' },
  { value: 'MEDICAL', label: 'Medical (DHA/EHS)' },
  { value: 'MOFA', label: 'MOFA - Attestation' },
  { value: 'TAWJEEH', label: 'TAWJEEH' },
  { value: 'ADMIN', label: 'Administrative' },
  { value: 'OTHER', label: 'Other' },
  // Legacy categories (backward compat with existing services)
  { value: 'VISA', label: 'Visa Services', legacy: true },
  { value: 'LABOR', label: 'Labor Services', legacy: true },
  { value: 'PRO', label: 'PRO Services', legacy: true },
  { value: 'DOCUMENT', label: 'Document Services', legacy: true },
  { value: 'COURIER', label: 'Courier & Delivery', legacy: true },
  { value: 'CONSULTATION', label: 'Consultation', legacy: true }
];

// Normalize snake_case API response to camelCase used by frontend components
const normalizeInternalService = (s) => ({
  ...s,
  serviceCharge: s.service_charge ?? s.serviceCharge ?? 0,
  taxOnServiceCharge: s.tax_on_service_charge ?? s.taxOnServiceCharge ?? 0,
  govtCharge: s.govt_charge ?? s.govtCharge ?? 0,
  createsTask: s.creates_task ?? s.createsTask ?? false,
  uniformPricing: s.uniform_pricing ?? s.uniformPricing,
  templateKey: s.template_key ?? s.templateKey ?? null,
  categoryId: s.category_id ?? s.categoryId ?? null,
  typingCenterId: s.typing_center_id ?? s.typingCenterId,
  type: 'INTERNAL'
});

const getTypingCenterId = () => {
  return (
    localStorage.getItem('typingCenterId') ||
    JSON.parse(localStorage.getItem('currentUser') || '{}')?.typingCenterId
  );
};

// When Thaklis features are disabled for the TC, the catalog shows only its own internal services.
const isThaklisDisabledSession = () => {
  try {
    return JSON.parse(localStorage.getItem('currentUser') || '{}')?.thaklisEnabled === false;
  } catch (e) {
    return false;
  }
};

// Also off when the Work Order Settings switch is off; mirrors the work-order service picker.
const isThaklisServicesOffSession = async () => {
  if (isThaklisDisabledSession()) return true;
  const typingCenterId = getTypingCenterId();
  if (!typingCenterId) return false;
  return !(await isThaklisServicesEnabled(typingCenterId));
};

const internalServiceService = {
  /**
   * Get all services (Thaklis + Internal combined)
   * @returns {Promise<Array>} Combined list of services
   */
  getAllServices: async () => {
    const typingCenterId = getTypingCenterId();

    // Get Thaklis services from API — fail gracefully. Skipped entirely when Thaklis is disabled.
    let thaklisServices = [];
    if (!(await isThaklisServicesOffSession())) {
      try {
        const response = await ApiService.getServices();
        thaklisServices = (response?.data?.data || []).map(s => ({
          id: s.id,
          code: s.code || '',
          name: s.name || s.service_name,
          description: s.description || '',
          category: s.category || 'VISA',
          serviceCharge: s.serviceCharge ?? s.service_charge ?? 0,
          taxOnServiceCharge: s.taxOnServiceCharge ?? 0,
          govtCharge: s.govtCharge ?? s.govt_charge ?? 0,
          createsTask: false,
          status: 'ACTIVE',
          type: 'THAKLIS'
        }));
      } catch (err) {
        console.warn('Unable to fetch Thaklis services:', err);
        thaklisServices = [];
      }
    }

    // Get internal services from backend API
    const internalResponse = await ApiService.get('/api/internal-services', {
      params: { typingCenterId }
    });
    const internalServices = (internalResponse?.data?.data || internalResponse?.data || []).map(normalizeInternalService);

    return [...thaklisServices, ...internalServices];
  },

  /**
   * Get only active internal services
   * @returns {Promise<Array>} List of active internal services
   */
  getInternalServices: async () => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.get('/api/internal-services', {
      params: { typingCenterId }
    });
    return (response?.data?.data || response?.data || []).map(normalizeInternalService);
  },

  /**
   * Get all internal services including inactive
   * @returns {Promise<Array>} All internal services
   */
  getAllInternalServices: async () => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.get('/api/internal-services', {
      params: { typingCenterId, includeInactive: true }
    });
    return (response?.data?.data || response?.data || []).map(normalizeInternalService);
  },

  /**
   * Get internal service by ID
   * @param {string|number} id - Service ID
   * @returns {Promise<Object>} Service object
   */
  getById: async (id) => {
    const response = await ApiService.get(`/api/internal-services/${id}`);
    const data = response?.data?.data || response?.data || null;
    return data ? normalizeInternalService(data) : null;
  },

  /**
   * Create new internal service
   * @param {Object} serviceData - Service data
   * @returns {Promise<Object>} Created service
   */
  create: async (serviceData) => {
    const typingCenterId = getTypingCenterId();
    const payload = {
      typingCenterId,
      name: serviceData.name,
      description: serviceData.description,
      // Omitted when the caller leaves it out, so an update without it keeps the current category.
      ...(serviceData.categoryId !== undefined && { categoryId: serviceData.categoryId || null }),
      serviceCharge: serviceData.serviceCharge,
      taxOnServiceCharge: serviceData.taxOnServiceCharge,
      govtCharge: serviceData.govtCharge,
      ...(serviceData.bankAccountId !== undefined && { bankAccountId: serviceData.bankAccountId || null }),
      createsTask: serviceData.createsTask,
      uniformPricing: serviceData.uniformPricing,
      status: serviceData.status || 'ACTIVE'
    };
    const response = await ApiService.post('/api/internal-services', payload);
    return response?.data?.data || response?.data;
  },

  /**
   * Update internal service
   * @param {string|number} id - Service ID
   * @param {Object} serviceData - Updated service data
   * @returns {Promise<Object>} Updated service
   */
  update: async (id, serviceData) => {
    const payload = {
      name: serviceData.name,
      description: serviceData.description,
      // Omitted when the caller leaves it out, so an update without it keeps the current category.
      ...(serviceData.categoryId !== undefined && { categoryId: serviceData.categoryId || null }),
      serviceCharge: serviceData.serviceCharge,
      taxOnServiceCharge: serviceData.taxOnServiceCharge,
      govtCharge: serviceData.govtCharge,
      ...(serviceData.bankAccountId !== undefined && { bankAccountId: serviceData.bankAccountId || null }),
      createsTask: serviceData.createsTask,
      uniformPricing: serviceData.uniformPricing,
      status: serviceData.status
    };
    const response = await ApiService.put(`/api/internal-services/${id}`, payload);
    return response?.data?.data || response?.data;
  },

  /**
   * Soft delete internal service via backend
   * @param {string|number} id - Service ID
   * @returns {Promise<void>}
   */
  delete: async (id) => {
    await ApiService.delete(`/api/internal-services/${id}`);
  },

  // ===== Per-organization pricing =====

  /**
   * Internal services offered to a given org with the price resolved for that org (uniform services +
   * services mapped to the org). Pass no orgId for the no-org (walk-in) case → uniform services only.
   */
  getServicesForOrganization: async (organizationId) => {
    const typingCenterId = getTypingCenterId();
    const params = { typingCenterId };
    if (organizationId) params.organizationId = organizationId;
    const response = await ApiService.get('/api/internal-services/for-organization', { params });
    return (response?.data?.data || []).map(normalizeInternalService);
  },

  /**
   * Services to offer when adding a WO/invoice line item for a given org: Thaklis services (skipped
   * when Thaklis is disabled) + internal services with the price RESOLVED for that org (uniform + mapped
   * to the org). Pass no orgId for the no-org (walk-in) case → uniform internal services only.
   */
  getServicesForLineItems: async (organizationId) => {
    let thaklisServices = [];
    if (!(await isThaklisServicesOffSession())) {
      try {
        const response = await ApiService.getServices();
        thaklisServices = (response?.data?.data || []).map(s => ({
          id: s.id,
          code: s.code || '',
          name: s.name || s.service_name,
          description: s.description || '',
          category: s.category || 'VISA',
          serviceCharge: s.serviceCharge ?? s.service_charge ?? 0,
          taxOnServiceCharge: s.taxOnServiceCharge ?? 0,
          govtCharge: s.govtCharge ?? s.govt_charge ?? 0,
          createsTask: false,
          status: 'ACTIVE',
          type: 'THAKLIS'
        }));
      } catch (err) {
        thaklisServices = [];
      }
    }
    const typingCenterId = getTypingCenterId();
    const params = { typingCenterId };
    if (organizationId) params.organizationId = organizationId;
    const resp = await ApiService.get('/api/internal-services/for-organization', { params });
    const internal = (resp?.data?.data || []).map(normalizeInternalService);
    return [...thaklisServices, ...internal];
  },

  /** Non-uniform (mappable) services for the Create-Mapping page. */
  getMappableServices: async () => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.get('/api/internal-services/mappable', { params: { typingCenterId } });
    return (response?.data?.data || []).map(normalizeInternalService);
  },

  /** Current mapping for (service, org): { exists, service_charge, tax_on_service_charge, govt_charge, default_* }. */
  lookupMapping: async (serviceId, organizationId) => {
    const response = await ApiService.get('/api/internal-services/mappings/lookup', {
      params: { serviceId, organizationId }
    });
    return response?.data?.data || null;
  },

  /** Create or update a (service, org) price mapping. */
  saveMapping: async ({ serviceId, organizationId, serviceCharge, taxOnServiceCharge, govtCharge }) => {
    const response = await ApiService.post('/api/internal-services/mappings', {
      serviceId, organizationId, serviceCharge, taxOnServiceCharge, govtCharge
    });
    return response?.data?.data || response?.data;
  },

  /** List mappings by organization or by service. */
  listMappings: async ({ organizationId, serviceId } = {}) => {
    const params = {};
    if (organizationId) params.organizationId = organizationId;
    if (serviceId) params.serviceId = serviceId;
    const response = await ApiService.get('/api/internal-services/mappings', { params });
    return response?.data?.data || [];
  },

  deleteMapping: async (id) => {
    await ApiService.delete(`/api/internal-services/mappings/${id}`);
  },

  /**
   * Bulk upsert mappings (service × org with charges)
   * @param {Array} mappings - [{serviceId, organizationId, serviceCharge, taxOnServiceCharge, govtCharge}]
   * @returns {Promise<Object>} { created, updated, errors }
   */
  bulkMappings: async (mappings) => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.post('/api/internal-services/mappings/bulk', {
      typingCenterId,
      mappings
    });
    return response?.data?.data || response?.data;
  },

  /**
   * Bulk import services from a template (UAE Standard, etc.)
   * @param {Object} templateData - { services: [...], groups: [...] }
   * @returns {Promise<Object>} { created, skipped, aliasesCreated, groupsCreated }
   */
  bulkImportTemplate: async (templateData) => {
    const typingCenterId = getTypingCenterId();
    const payload = {
      typingCenterId,
      services: templateData.services,
      groups: templateData.groups || []
    };
    const response = await ApiService.post('/api/internal-services/bulk', payload);
    return response?.data?.data || response?.data;
  },

  // ===== Service Groups =====

  getServiceGroups: async () => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.get('/api/service-groups', { params: { typingCenterId } });
    return response?.data?.data || [];
  },

  createServiceGroup: async (data) => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.post('/api/service-groups', { typingCenterId, ...data });
    return response?.data?.data || response?.data;
  },

  updateServiceGroup: async (id, data) => {
    const response = await ApiService.put(`/api/service-groups/${id}`, data);
    return response?.data?.data || response?.data;
  },

  deleteServiceGroup: async (id) => {
    await ApiService.delete(`/api/service-groups/${id}`);
  },

  /**
   * Get existing template keys for this TC (to show "already imported" in dialog)
   * @returns {Promise<string[]>} List of template keys already imported
   */
  getExistingTemplateKeys: async () => {
    const services = await internalServiceService.getAllInternalServices();
    return services
      .filter(s => s.templateKey || s.template_key)
      .map(s => s.templateKey || s.template_key);
  },

  /**
   * Get service statistics from a combined services list
   * @param {Array} services - List of services
   * @returns {Object} Statistics
   */
  getStatistics: (services) => {
    const activeServices = services.filter(s => s.status === 'ACTIVE');
    return {
      total: activeServices.length,
      thaklis: activeServices.filter(s => s.type === 'THAKLIS').length,
      internal: activeServices.filter(s => s.type === 'INTERNAL').length,
      withTasks: activeServices.filter(s => s.createsTask).length,
      categories: [...new Set(activeServices.map(s => s.category))].length
    };
  },

  /**
   * Get category label from value
   * @param {string} value - Category value
   * @returns {string} Category label
   */
  getCategoryLabel: (value) => {
    const category = SERVICE_CATEGORIES.find(c => c.value === value);
    return category?.label || value;
  },

  /**
   * Format currency
   * @param {number} amount - Amount
   * @returns {string} Formatted currency string
   */
  formatCurrency: (amount) => {
    if (amount == null || isNaN(amount)) return 'AED 0.00';
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount);
  }
};

export default internalServiceService;
